/**
 * Role Candidates page - View dynamically ranked candidates for a role
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { roleAPI, resumeBankAPI, Role, CandidateEvaluation } from '../api/client';
import TopRibbon from '../components/TopRibbon';
import CandidateRow from '../components/CandidateRow';
import CandidatePagination from '../components/CandidatePagination';
import './RoleCandidates.css';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

const RoleCandidates: React.FC = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();

  const [role, setRole] = useState<Role | null>(null);
  const [candidates, setCandidates] = useState<CandidateEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const pollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [letterGradeFilter, setLetterGradeFilter] = useState<string>('all');
  const [minScoreFilter, setMinScoreFilter] = useState<number>(0);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const filteredCandidates = useMemo(() => {
    if (!searchQuery.trim()) return candidates;
    const q = searchQuery.toLowerCase().trim();
    return candidates.filter((c) => {
      const name = (c.full_name || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      const phone = (c.phone || '').toLowerCase();
      return name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [candidates, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredCandidates.length / pageSize));
  const paginatedCandidates = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCandidates.slice(start, start + pageSize);
  }, [filteredCandidates, currentPage, pageSize]);

  // Reset to page 1 when filters or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [letterGradeFilter, minScoreFilter, pageSize, searchQuery]);

  const fetchRole = React.useCallback(async () => {
    if (!roleId) return;
    try {
      const response = await roleAPI.getRole(roleId);
      setRole(response.data);
    } catch (error) {
      console.error('Failed to fetch role:', error);
    }
  }, [roleId]);

  const fetchCandidates = React.useCallback(async () => {
    if (!roleId) return;
    setLoading(true);
    try {
      const params: any = { limit: 2000 };
      if (minScoreFilter > 0) params.min_score = minScoreFilter;
      if (letterGradeFilter !== 'all') params.letter_grade = letterGradeFilter;

      const response = await roleAPI.getCandidates(roleId, params);
      setCandidates(response.data);
    } catch (error) {
      console.error('Failed to fetch candidates:', error);
    } finally {
      setLoading(false);
    }
  }, [roleId, letterGradeFilter, minScoreFilter]);

  useEffect(() => {
    if (roleId) {
      fetchRole();
      fetchCandidates();
    }
  }, [roleId, letterGradeFilter, minScoreFilter, fetchRole, fetchCandidates]);

  // Auto-refresh when processing
  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (role?.is_processing) {
      console.log('Processing active - starting 15s polling');
      const interval = setInterval(() => {
        console.log('Polling for new candidates...');
        fetchRole();
        fetchCandidates();
      }, 15000);

      pollIntervalRef.current = interval;
      return () => {
        console.log('Stopping polling');
        clearInterval(interval);
      };
    }
  }, [role?.is_processing, roleId, fetchRole, fetchCandidates]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await roleAPI.downloadCSV(roleId!);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `role_${roleId}_candidates.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to export CSV:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to export CSV. Please try again.';
      alert(message);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAllCandidates = async () => {
    if (!window.confirm(
      `Delete all ${candidates.length} candidates for this role?\n\n` +
      'This will permanently remove all evaluations. This cannot be undone.'
    )) {
      return;
    }

    setDeleting(true);
    try {
      await roleAPI.deleteAllCandidates(roleId!);
      await fetchCandidates();
      await fetchRole();
      alert('All candidates deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete candidates:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to delete candidates';
      alert(message);
    } finally {
      setDeleting(false);
    }
  };

  const handleViewResume = async (resumeId: string) => {
    try {
      const response = await resumeBankAPI.downloadResume(resumeId);

      if (response.data && typeof response.data === 'object' && response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      } else {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      }
    } catch (error: any) {
      console.error('Failed to open resume:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to open resume. Please try again.';
      alert(message);
    }
  };

  const startIdx = (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, filteredCandidates.length);

  return (
    <>
      <TopRibbon />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', paddingTop: '64px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px 20px' }}>
          {/* Back button */}
          <button
            onClick={() => navigate('/roles')}
            style={{
              marginBottom: '24px',
              padding: '10px 20px',
              background: 'white',
              color: '#2d5016',
              border: '2px solid #2d5016',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2d5016';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#2d5016';
            }}
          >
            &larr; Back to Roles
          </button>

          {/* Role info + filters bar */}
          {role && (
            <div style={{
              marginBottom: '24px',
              padding: '20px',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '24px',
              flexWrap: 'wrap'
            }}>
              {/* Left: Role name and meta inline */}
              <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#1f2937' }}>
                  {role.title}
                </h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: '#6b7280' }}>
                  <span><strong>{candidates.length}</strong> candidates</span>
                  {role.is_criteria_locked && (
                    <span style={{
                      padding: '4px 8px',
                      background: '#fef3c7',
                      color: '#92400e',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      Criteria Locked
                    </span>
                  )}
                </div>
              </div>

              {/* Right: Filters and actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Search name, email, phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
                    fontSize: '14px', width: '220px', outline: 'none'
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#2d5016'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Min Score:</label>
                  <select
                    value={minScoreFilter}
                    onChange={(e) => setMinScoreFilter(Number(e.target.value))}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: 'white', cursor: 'pointer' }}
                  >
                    <option value={0}>All</option>
                    <option value={90}>90+</option>
                    <option value={80}>80+</option>
                    <option value={70}>70+</option>
                    <option value={60}>60+</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Grade:</label>
                  <select
                    value={letterGradeFilter}
                    onChange={(e) => setLetterGradeFilter(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: 'white', cursor: 'pointer' }}
                  >
                    <option value="all">All</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: 'white', cursor: 'pointer' }}
                  >
                    {PAGE_SIZE_OPTIONS.map(n => (
                      <option key={n} value={n}>{n} per page</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => navigate(`/roles/${roleId}/upload`)}
                  style={{
                    padding: '8px 16px', background: 'white', color: '#2d5016', border: '1px solid #2d5016',
                    borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#2d5016'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#2d5016'; }}
                >
                  + Upload More
                </button>
                <button
                  onClick={handleExportCSV}
                  disabled={candidates.length === 0 || exporting}
                  style={{
                    padding: '8px 16px',
                    background: candidates.length === 0 || exporting ? '#d1d5db' : '#2d5016',
                    color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600,
                    cursor: candidates.length === 0 || exporting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#3d6b1f')}
                  onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#2d5016')}
                >
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </button>
                <button
                  onClick={handleDeleteAllCandidates}
                  disabled={candidates.length === 0 || deleting}
                  style={{
                    padding: '8px 16px', background: 'white',
                    color: candidates.length === 0 || deleting ? '#9ca3af' : '#dc2626',
                    border: `1px solid ${candidates.length === 0 || deleting ? '#d1d5db' : '#fca5a5'}`,
                    borderRadius: '6px', fontSize: '14px', fontWeight: 600,
                    cursor: candidates.length === 0 || deleting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#fef2f2')}
                  onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = 'white')}
                >
                  {deleting ? 'Deleting...' : 'Clear All'}
                </button>
              </div>
            </div>
          )}

          {/* Candidates table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px 20px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
              <div style={{
                width: '48px', height: '48px', border: '4px solid #e5e7eb', borderTopColor: '#2d5016',
                borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px'
              }}></div>
              <p style={{ color: '#6b7280' }}>Loading candidates...</p>
            </div>
          ) : candidates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 20px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
              <h2 style={{ margin: '0 0 12px 0', fontSize: '24px', color: '#1a1a1a' }}>No candidates yet</h2>
              <p style={{ margin: '0 0 24px 0', fontSize: '16px', color: '#6b7280' }}>
                Upload a resume binder to start evaluating candidates for this role.
              </p>
              <button
                onClick={() => navigate(`/roles/${roleId}/upload`)}
                style={{ padding: '12px 24px', background: '#2d5016', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' }}
              >
                Upload First Binder
              </button>
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
              <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: '14px', background: '#f9fafb' }}>Rank</th>
                      <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '14px', background: '#f9fafb' }}>Name</th>
                      <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '14px', background: '#f9fafb' }}>Contact</th>
                      <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: '14px', background: '#f9fafb' }}>Score</th>
                      <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: '14px', background: '#f9fafb' }}>Grade</th>
                      <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: '14px', background: '#f9fafb' }}>Review</th>
                      <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: '14px', background: '#f9fafb' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCandidates.map((candidate) => (
                      <CandidateRow key={candidate.id} candidate={candidate} onViewResume={handleViewResume} />
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <CandidatePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  startIdx={startIdx}
                  endIdx={endIdx}
                  totalCount={filteredCandidates.length}
                  onPageChange={setCurrentPage}
                />
              )}

              <div style={{
                padding: '16px', background: '#eff6ff', borderTop: '1px solid #e5e7eb', fontSize: '14px', color: '#1e40af'
              }}>
                <strong>Dynamic Ranking:</strong> Rankings update automatically as new candidates are added.
                Ranks are calculated live based on scores (not stored).
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default RoleCandidates;
